import React, { useEffect, useState } from "react";
import {
  CardContent,
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import LoadingSpinner from "../../../LoadingSpinner";
import {
  useGetAllUserListQuery,
  useGetLoginAndLogoutReportQuery,
  useLazyGetUserLoginLogoutReportDownloadQuery,
} from "../../../../Services/reports/tickets/ReportTickets";
import { toast } from "react-toastify";
import CsvDownloader from "react-csv-downloader";
import { AUTH_TOKEN } from "../../../../constants";

const getTodayDateString = () => new Date().toISOString().split("T")[0];

const isCurrentDateRange = (from: string, to: string) => {
  const today = getTodayDateString();
  return from === today && to === today;
};

type ReportMode = "live" | "historical" | null;

const UserLoginReport = () => {
  const navigate = useNavigate();
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");
  const [params, setParams] = useState<any>(null);
  const [selectedUser, setSelectedUser] = useState<any>([]);
  const [selectedUserParam, setSelectedUserParam] = useState<string[]>([]);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 20,
    page: 0,
  });
  const [localLoading, setLocalLoading] = useState(false);
  const [dataRows, setDataRows] = useState<any[]>([]);
  const { data: userData, isLoading: userLoading } = useGetAllUserListQuery({});
  const [selectedAssignee, setSelectedAssignee] = useState<string>("");
  const [reportFetching, setReportFetching] = useState(false);
  const [reportMode, setReportMode] = useState<ReportMode>(null);
  const [totalRowsFromApi, setTotalRowsFromApi] = useState<number | null>(null);

  const {
    data: liveLoginReportData,
    isFetching: liveReportFetching,
    status: liveReportStatus,
  } = useGetLoginAndLogoutReportQuery(params, {
    skip: !params || reportMode !== "live",
  });

  const [triggerUserLoginLogoutDownload, { isFetching: downloadLoading }] =
    useLazyGetUserLoginLogoutReportDownloadQuery();

  const reportingBaseUrl =
    process.env.REACT_APP_REPORTING_SERVER_URL || "https://reporting.bluewhirl.org";

  const handleChange: any = (event: React.ChangeEvent<{ value: unknown }>) => {
    setSelectedAssignee(event.target.value as string);
  };

  const [theUser, setTheUser] = useState<any>([]);
  useEffect(() => {
    const transformedData = userData?.map((user: any) => ({
      id: user.id,
      username: user.username,
    }));
    if (Array.isArray(userData)) {
      setTheUser(transformedData);
    } else {
      setTheUser([]);
    }
  }, [userData]);

  useEffect(() => {
    const token = localStorage.getItem(AUTH_TOKEN);
    if (!params || reportMode !== "historical") {
      if (reportMode !== "historical") {
        setDataRows([]);
        setTotalRowsFromApi(null);
      }
      return;
    }

    const controller = new AbortController();
    setLocalLoading(true);
    setReportFetching(true);

    const queryParams: Record<string, string | number | boolean> = {
      from_date: params.gte,
      to_date: params.lte,
      page: params.page ?? 1,
      page_size: params.page_size ?? 20,
      download: false,
    };
    if (params.user_id != null && params.user_id !== "" && params.user_id !== "All") {
      queryParams.user_id = params.user_id;
    }

    axios
      .get(`${reportingBaseUrl}/telephony/user-login-logout`, {
        params: queryParams,
        signal: controller.signal,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((res) => {
        const data = res?.data;
        const results = Array.isArray(data?.results)
          ? data.results
          : Array.isArray(data?.data)
            ? data.data
            : Array.isArray(data)
              ? data
              : [];
        const totalRows =
          data?.total_rows ?? data?.count ?? data?.total ?? results.length;
        setTotalRowsFromApi(
          typeof totalRows === "number" ? totalRows : results.length
        );

        const serverPage = data?.current_page ?? data?.page ?? params.page ?? 1;
        const serverPageSize = data?.page_size ?? params.page_size ?? 20;
        const apiPage = Number(serverPage);
        const pageSizeNum = Number(serverPageSize);
        setPaginationModel({
          page: Math.max(0, apiPage - 1),
          pageSize: pageSizeNum,
        });
        const rowOffset = (apiPage - 1) * pageSizeNum;

        if (results.length > 0) {
          const transformedRows = results.map((item: any, index: number) => {
            const sr = rowOffset + index + 1;
            return {
              id: sr,
              srNo: sr,
              agentName: item.agent_name || "-",
              department: item.team_name || "-",
              agentRole: item.agent_role || "-",
              campaignName: item.campaigns
                ? item.campaigns.split(",").join(", ")
                : "-",
              loginDate: formatDate(item.login_time) || "-",
              loginTime: formatTime(item.login_time) || "-",
              logoutDate: formatDate(item.logout_time) || "-",
              logoutTime: formatTime(item.logout_time) || "-",
              duration: item.login_duration
                ? formatDuration(item.login_duration)
                : "-",
              logoutReason: item.logout_reason || "-",
            };
          });
          setDataRows(transformedRows);
        } else {
          setDataRows([]);
        }
      })
      .catch((err) => {
        if (axios.isCancel(err)) return;
        toast.error(
          err?.response?.data?.message ||
            "Failed to fetch User Login/Logout report"
        );
        setDataRows([]);
      })
      .finally(() => {
        setLocalLoading(false);
        setReportFetching(false);
      });

    return () => controller.abort();
  }, [params, reportingBaseUrl, reportMode]);

  useEffect(() => {
    if (reportMode !== "live") return;
    if (params) setLocalLoading(true);
  }, [params, reportMode]);

  useEffect(() => {
    if (reportMode !== "live") return;
    if (!Array.isArray(liveLoginReportData)) {
      setDataRows([]);
      setLocalLoading(false);
      return;
    }
    const transformedRows = liveLoginReportData.map((item: any, index: number) => ({
      id: index + 1,
      srNo: index + 1,
      agentName: item.agent_name || "-",
      department: item.team_name || "-",
      agentRole: item.agent_role || "-",
      campaignName: item.campaigns ? item.campaigns.split(",").join(", ") : "-",
      loginDate: item.login_time_display || formatDate(item.login_time) || "-",
      loginTime: formatTime(item.login_time) || "-",
      logoutDate: item.logout_time_display || formatDate(item.logout_time) || "-",
      logoutTime: formatTime(item.logout_time) || "-",
      duration: item.login_duration ? formatDuration(item.login_duration) : "-",
      logoutReason: item.logout_reason || "-",
    }));
    setDataRows(transformedRows);
    setLocalLoading(false);
  }, [liveLoginReportData, reportMode]);

  useEffect(() => {
    const dropdownHeadings = document.querySelectorAll(".dropdown-heading");
    dropdownHeadings.forEach((heading: any) => {
      heading.style.height = "50px";
    });
  }, []);

  function formatDate(datetime: any) {
    if (!datetime) return "No data found";
    const dateObj = new Date(datetime);
    const day = String(dateObj.getDate()).padStart(2, "0");
    const month = String(dateObj.getMonth() + 1).padStart(2, "0");
    const year = String(dateObj.getFullYear()).slice(-2);
    return `${day}/${month}/${year}`;
  }

  function formatTime(datetime: any) {
    if (!datetime) return "No data found";
    const dateObj = new Date(datetime);
    const hours = String(dateObj.getHours()).padStart(2, "0");
    const minutes = String(dateObj.getMinutes()).padStart(2, "0");
    const seconds = String(dateObj.getSeconds()).padStart(2, "0");
    return `${hours}:${minutes}:${seconds}`;
  }

  const formatDuration = (duration: any) => {
    if (!duration || typeof duration !== "string") return "-";
    const parts = duration.split(":").map((part: string) => part.padStart(2, "0"));
    return parts.join(":");
  };

  const columns: GridColDef[] = [
    {
      field: "srNo",
      headerName: "Sr.No",
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "agentName",
      headerName: "User Name",
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "department",
      headerName: "Department Name",
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "agentRole",
      headerName: "User Type",
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "campaignName",
      headerName: "Queue Name",
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "loginDate",
      headerName: "Login Date",
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "loginTime",
      headerName: "Login Time",
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "logoutDate",
      headerName: "Logout Date",
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "logoutTime",
      headerName: "Logout Time",
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "duration",
      headerName: "Total Login Duration",
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "logoutReason",
      headerName: "Reason",
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
  ];

  const handleDateFromChange = (event: any) => {
    setDateFrom(event.target.value);
  };

  const handleDateToChange = (event: any) => {
    setDateTo(event.target.value);
  };
  const handleSubmit = () => {
    if (!dateFrom) {
      toast.error('Please select "From Date"');
      return;
    }
    if (!dateTo) {
      toast.error('Please select "To Date"');
      return;
    }
    if (!isCurrentDateRange(dateFrom, dateTo) && !selectedAssignee) {
      toast.error("Please select Assignee");
      return;
    }

    const queryParams: any = {
      gte: dateFrom,
      lte: dateTo,
      page: 1,
      page_size: paginationModel.pageSize,
    };
    if (selectedAssignee && selectedAssignee !== "All") {
      queryParams.user_id = selectedAssignee;
    }
    setReportMode(isCurrentDateRange(dateFrom, dateTo) ? "live" : "historical");
    setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
    setParams(queryParams);
  };

  const handlePaginationChange = (newPaginationModel: any) => {
    setPaginationModel(newPaginationModel);
    if (!params) return;
    if (reportMode === "live") return;
    setParams({
      ...params,
      page: newPaginationModel.page + 1,
      page_size: newPaginationModel.pageSize,
    });
  };

  const gridLoading =
    reportMode === "live" ? localLoading || liveReportFetching : localLoading;
  const showSpinner =
    userLoading ||
    (reportMode === "live"
      ? liveReportStatus === "pending"
      : reportFetching || localLoading);

   const handleDownload = async () => {
    if (!params) {
      toast.error("Please apply filters and submit before generating the report.");
      return;
    }

    const downloadPayload: Record<string, string | number> = {
      from_date: params.gte,
      to_date: params.lte,
      page: 1,
      page_size: totalRowsFromApi ?? 99999,
    };
    if (
      params.user_id != null &&
      params.user_id !== "" &&
      params.user_id !== "All"
    ) {
      downloadPayload.user_id = params.user_id;
    }

    try {
      await triggerUserLoginLogoutDownload(downloadPayload).unwrap();
      toast.success("Report generation in progress. Download from exported report section once ready");
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to queue report generation.");
    }
  };

  const resetFilters = () => {
    setDateFrom("");
    setDateTo("");
    setParams(null);
    setReportMode(null);
    setSelectedUser([]);
    setSelectedAssignee("");
    setDataRows([]);
    setTotalRowsFromApi(null);
    setPaginationModel({ page: 0, pageSize: 20 });
  };

  useEffect(() => {
    setSelectedUserParam(selectedUser);
  }, [selectedUser]);

  const ITEM_HEIGHT = 60;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 150,
      },
    },
  };
  const isAllSelected = selectedUser.length === theUser.length;

  return (
    <div id="kt_app_content_container" className="app-container mx-5">
      <div className="card card-flush">
        <div
          className="card-header align-items-center py-1 gap-2 gap-md-5"
          style={{ justifyContent: "flex-start" }}
        >
          {/* <img
            src="https://insurance.circleonecrm.com/assets/arrow_back.svg"
            alt="Back"
            style={{ marginBottom: "-2px", cursor: "pointer" }}
            className="ng-star-inserted"
            onClick={() => {
              navigate("/reports/telephony/reports-list");
            }}
          /> */}
          <button
            className="btn btn-link mb-2"
            onClick={() => {
              navigate("/reports/telephony/reports-list");
            }}
            style={{ marginRight: "-17px" }}
          >
            <i
              className="bi bi-arrow-90deg-left"
              style={{
                fontSize: "20px",
                color: "black",
                cursor: "pointer",
              }}
            ></i>
          </button>
          <span className="fs-3 text-gray-800 fw-bold ms-3">
            User Login/Logout
          </span>
        </div>

        <div
          className="card-toolbar flex-row-fluid justify-content-start gap-5 d-flex mb-10"
          style={{ marginLeft: "30px" }}
        >
          <div className="w-100 mw-150px">
            <TextField
              id="date-from"
              label="From"
              type="date"
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                inputProps: {
                  max: new Date().toISOString().split("T")[0], // Restrict to today or earlier
                  style: { textTransform: "uppercase" }, // Optional: uppercase input text
                },
              }}
              fullWidth
              value={dateFrom}
              onChange={handleDateFromChange}
            />
          </div>
          <div className="w-100 mw-150px">
            <TextField
              id="date-to"
              label="To"
              type="date"
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                inputProps: {
                  max: new Date().toISOString().split("T")[0],
                  style: { textTransform: "uppercase" }, // Optional: uppercase input text
                },
              }}
              fullWidth
              value={dateTo}
              onChange={handleDateToChange}
            />
          </div>

          <div className="w-100 mw-150px">
            <FormControl sx={{ width: "200px" }}>
              <InputLabel id="Assignee-select-label">Assignee</InputLabel>
              <Select
                labelId="Assignee-select-label"
                id="Assignee-select"
                label="Assignee"
                MenuProps={MenuProps}
                value={selectedAssignee}
                onChange={handleChange}
              >
                <MenuItem key="all" value="All">
                  All
                </MenuItem>
                {theUser?.map((assignee: any) => (
                  <MenuItem key={assignee.id} value={assignee.id}>
                    {assignee.username}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          <div className="ms-auto d-flex gap-3" style={{ marginRight: "20px" }}>
            <button className="btn btn-primary" onClick={handleSubmit}>
              Submit
            </button>

            {reportMode === "live" ? (
              <button
                className="btn btn-primary"
                disabled={!params || dataRows.length === 0}
              >
                <CsvDownloader
                  filename="User Login Logout Report"
                  datas={dataRows}
                  columns={columns.map((col) => ({
                    id: col.field,
                    displayName: col.headerName,
                  }))}
                  separator=","
                  wrapColumnChar={`"`}
                >
                  Download
                </CsvDownloader>
              </button>
            ) : (
              <button
                className="btn btn-primary"
                onClick={handleDownload}
                disabled={!params || downloadLoading}
              >
                {downloadLoading ? "Loading..." : "Download"}
              </button>
            )}

            <button className="btn btn-link" onClick={resetFilters}>
              <i className="bi bi-funnel fs-2 text-dark"></i>
            </button>
          </div>
        </div>

        <CardContent style={{ width: "100%" }}>
          {showSpinner ? (
            <LoadingSpinner />
          ) : (
            <DataGrid
              rows={dataRows}
              columns={columns}
              pagination
              {...(reportMode === "historical"
                ? {
                    paginationMode: "server" as const,
                    rowCount: totalRowsFromApi ?? 0,
                  }
                : {})}
              paginationModel={paginationModel}
              onPaginationModelChange={handlePaginationChange}
              loading={gridLoading}
              disableColumnMenu
              pageSizeOptions={[5, 10, 15, 20]}
              sx={{
                "& .MuiDataGrid-columnHeader": {
                  backgroundColor: "lightgray",
                  "&:last-child": {
                    justifyContent: "flex-end",
                  },
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontWeight: "bold",
                },
                "& .MuiDataGrid-columnsContainer": {
                  backgroundColor: "lightgray",
                },
                // Target the pagination select and actions by stable component classes
                "& .MuiTablePagination-select, .MuiTablePagination-actions": {
                  marginBottom: "10px",
                },
                // Target the DataGrid overlay wrapper with its stable class
                "& .MuiDataGrid-overlayWrapper": {
                  height: "50px",
                },
              }}
            />
          )}
        </CardContent>
      </div>
    </div>
  );
};

export default UserLoginReport;
